import React, { useState, useEffect } from 'react';
import { 
  CogIcon, 
  ServerIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { systemAPI } from '../services/api';
import { SystemStats } from '../types';
import { toast } from 'react-toastify';

const SystemSettings: React.FC = () => {
  const [systemStats, setSystemStats] = useState<SystemStats>({
    uptime: '0 days',
    memoryUsage: 0,
    diskUsage: 0,
    activeConnections: 0,
    requestsPerMinute: 0,
    errorRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSystemStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(loadSystemStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSystemStats = async () => {
    try {
      const response = await systemAPI.getSystemStats();
      setSystemStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load system stats');
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      await systemAPI.backupData();
      toast.success('Backup initiated successfully');
    } catch (error) {
      toast.error('Failed to initiate backup');
    }
  };

  const getStatusColor = (value: number, thresholds: { warning: number; danger: number }) => {
    if (value >= thresholds.danger) return 'text-red-600 dark:text-red-400';
    if (value >= thresholds.warning) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getStatusIcon = (value: number, thresholds: { warning: number; danger: number }) => {
    if (value >= thresholds.danger) return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
    if (value >= thresholds.warning) return <ClockIcon className="w-5 h-5 text-yellow-500" />;
    return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">System Settings</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Monitor system health, performance, and manage platform settings
        </p>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="card-admin p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">System Uptime</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{systemStats.uptime}</p>
            </div>
            <ServerIcon className="w-8 h-8 text-admin-600" />
          </div>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Memory Usage</p>
              <p className={`text-2xl font-bold ${getStatusColor(systemStats.memoryUsage, { warning: 70, danger: 85 })}`}>
                {systemStats.memoryUsage}%
              </p>
            </div>
            <div className="flex items-center">
              {getStatusIcon(systemStats.memoryUsage, { warning: 70, danger: 85 })}
            </div>
          </div>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Disk Usage</p>
              <p className={`text-2xl font-bold ${getStatusColor(systemStats.diskUsage, { warning: 80, danger: 90 })}`}>
                {systemStats.diskUsage}%
              </p>
            </div>
            <div className="flex items-center">
              {getStatusIcon(systemStats.diskUsage, { warning: 80, danger: 90 })}
            </div>
          </div>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Connections</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{systemStats.activeConnections}</p>
            </div>
            <CircleStackIcon className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Requests/Min</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{systemStats.requestsPerMinute}</p>
            </div>
            <ClockIcon className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Error Rate</p>
              <p className={`text-2xl font-bold ${getStatusColor(systemStats.errorRate, { warning: 2, danger: 5 })}`}>
                {systemStats.errorRate}%
              </p>
            </div>
            <div className="flex items-center">
              {getStatusIcon(systemStats.errorRate, { warning: 2, danger: 5 })}
            </div>
          </div>
        </div>
      </div>

      {/* System Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-admin p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ShieldCheckIcon className="w-6 h-6 text-admin-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Data Backup</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Create a backup of all system data including user profiles, company information, and platform settings.
          </p>
          <button onClick={handleBackup} className="btn-admin">
            Initiate Backup
          </button>
        </div>

        <div className="card-admin p-6">
          <div className="flex items-center space-x-3 mb-4">
            <CogIcon className="w-6 h-6 text-admin-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Maintenance</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Schedule maintenance windows and system updates to ensure optimal performance.
          </p>
          <button className="btn-admin-secondary">
            Schedule Maintenance
          </button>
        </div>
      </div>

      {/* Platform Settings */}
      <div className="card-admin p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Platform Settings</h3>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Maximum File Upload Size (MB)
              </label>
              <input
                type="number"
                defaultValue={10}
                className="input-admin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                defaultValue={60}
                className="input-admin"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                defaultChecked
                className="rounded border-gray-300 text-admin-600 focus:ring-admin-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable email notifications for new user registrations
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                defaultChecked
                className="rounded border-gray-300 text-admin-600 focus:ring-admin-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Require email verification for new accounts
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-admin-600 focus:ring-admin-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable maintenance mode
              </span>
            </label>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button className="btn-admin mr-3">Save Settings</button>
            <button className="btn-admin-secondary">Reset to Defaults</button>
          </div>
        </div>
      </div>

      {/* Recent System Events */}
      <div className="card-admin p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent System Events</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
            <div className="flex-1">
              <p className="text-sm text-gray-900 dark:text-white">System backup completed successfully</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">2 hours ago</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <ServerIcon className="w-5 h-5 text-blue-500" />
            <div className="flex-1">
              <p className="text-sm text-gray-900 dark:text-white">Database optimization completed</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">6 hours ago</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
            <div className="flex-1">
              <p className="text-sm text-gray-900 dark:text-white">High memory usage detected</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">1 day ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;